import { useEffect, useRef } from 'react';

import { api } from '../../lib/api';
import { usePlayer } from '../../stores/player';

/**
 * A play counts at 30 seconds, or a quarter of the track (HANDOFF §4.5).
 *
 * The same rule the server applies. Duplicated deliberately rather than fetched:
 * the server is still the one that decides, but a client using a different
 * threshold would fire requests that are always rejected, which is worse than
 * two copies of one constant.
 */
const MIN_MS = 30_000;
const MIN_FRACTION = 0.25;

/**
 * Report a play once somebody has actually listened to enough of a track.
 *
 * ## Time listened, not the position of the playhead
 *
 * `currentTime` is where the head is, which is not the same thing at all.
 * Dragging to 3:00 of a four-minute track sets `currentTime` to 180 without a
 * second of it having been heard, and a threshold on the playhead would count
 * that as a play - making the charts a measure of how easy a waveform is to
 * click on.
 *
 * So this accumulates *forward progress while playing*: the delta between
 * consecutive `timeupdate` events, discarded when it is negative (a rewind) or
 * implausibly large (a seek forward, or a tab that was backgrounded and had its
 * timers throttled). What survives is time that really elapsed with audio
 * coming out.
 *
 * ## Fired once per load
 *
 * `loadToken` rather than the track id, so replaying the same track deliberately
 * starts a fresh count - and so a track that appears twice in a queue is
 * measured twice. The server's hour bucket is what decides whether the second
 * one counts; that is not this component's decision to make.
 */
export function usePlayReporting(audioRef: React.RefObject<HTMLAudioElement | null>) {
  const trackId = usePlayer((state) => state.track?.id ?? null);
  const durationMs = usePlayer((state) => state.track?.duration_ms ?? null);
  const source = usePlayer((state) => state.source);
  const loadToken = usePlayer((state) => state.loadToken);

  const listened = useRef(0);
  const lastPosition = useRef(0);
  const reported = useRef(false);

  // A new load is a new count, whatever it is a load of.
  useEffect(() => {
    listened.current = 0;
    lastPosition.current = 0;
    reported.current = false;
  }, [loadToken]);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio || trackId === null) return;

    const onTime = () => {
      const delta = audio.currentTime - lastPosition.current;
      lastPosition.current = audio.currentTime;

      /*
       * Between a quarter of a second and five.
       *
       * `timeupdate` fires roughly four times a second, so a normal delta is
       * about 250ms. Anything larger is a seek or a throttled background tab,
       * and anything negative is a rewind - none of which is time somebody
       * spent listening. The upper bound is generous rather than tight because
       * a brief stall while buffering is real listening either side of it.
       */
      if (delta <= 0 || delta > 5) return;

      listened.current += delta * 1000;

      if (reported.current) return;

      const threshold = durationMs === null
        ? MIN_MS
        : Math.min(MIN_MS, durationMs * MIN_FRACTION);

      if (listened.current < threshold) return;

      // Set before the request, not after. A slow response must not let a
      // second `timeupdate` fire the same play again.
      reported.current = true;

      /*
       * `keepalive`, not `sendBeacon`.
       *
       * §4.5 says sendBeacon, and it is the wrong tool here for one specific
       * reason: it cannot set headers, so it cannot carry the bearer token, and
       * every signed-in play would be recorded as anonymous. `fetch` with
       * `keepalive: true` survives page unload the same way and does support
       * headers.
       *
       * Failure is swallowed. A play that did not record is not something the
       * listener can act on, and an error message over the top of the music
       * would be absurd.
       */
      void api
        .post(
          `/tracks/${trackId}/play`,
          { ms_played: Math.round(listened.current), source },
          { keepalive: true },
        )
        .catch(() => {});
    };

    audio.addEventListener('timeupdate', onTime);

    return () => audio.removeEventListener('timeupdate', onTime);
  }, [audioRef, trackId, durationMs, source]);
}
